  // ...existing code...
import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, Button } from "@mui/material";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";

/**
 * Componente para mostrar un bloque de texto enriquecido (RichText) de Contentful.
 * 
 * @param {object} props
 * @param {object} props.content - El contenido RichText de Contentful (documento JSON).
 * @param {string} [props.title] - Título opcional para el bloque.
 */
const RichTextBlock = ({ content, title, color }) => {
  // Debug: imprimir el contenido recibido y los assets mapeados
  if (!content) return null;

  // Buscar assets incluidos en el rich text (Contentful v10+ usa content.links.assets.block)
  const assetMap = {};
  if (content.links && content.links.assets && content.links.assets.block) {
    for (const asset of content.links.assets.block) {
      assetMap[asset.sys.id] = asset;
    }
    // Imprimir el assetMap completo después de mapear
  }

  // Usar el color de la palette primary de MUI
  const headerColor = (typeof window !== 'undefined' && window.__MUI_USE_THEME__ && window.__MUI_USE_THEME__.palette?.primary?.main)
    || '#003366';

  const options = {
    renderNode: {
      'embedded-asset-block': (node) => {
        let asset = assetMap[node.data.target.sys.id];
        if (!asset) asset = node.data.target;
        if (!asset) return null;
        // Obtener el mimeType correctamente
        const mimeType = asset.contentType || asset.mimeType || asset.fields?.file?.contentType || '';
        // Obtener la url correctamente
        let url = asset.url || asset.fields?.file?.url || '';
        if (url && url.startsWith('//')) url = 'https:' + url;
        // Obtener el título correctamente
        const title = asset.title || asset.fields?.title || '';
        const description = asset.description || asset.fields?.description || '';
        if (mimeType.startsWith('image/')) {
          return (
            <Box sx={{ my: 2, textAlign: 'center' }}>
              <img
                src={url}
                alt={title || description}
                style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 8 }}
              />
            </Box>
          );
        }
        // PDF u otros tipos
        if (mimeType === 'application/pdf') {
          return (
            <Box sx={{ my: 2, textAlign: 'center' }}>
              <Button
                variant="outlined"
                color="primary"
                href={asset.url || asset.fields?.file?.url || ''}
                target="_blank"
                rel="noopener noreferrer"
              >
                Ver PDF: {asset.title || 'Documento'}
              </Button>
            </Box>
          );
        }
        // Otros tipos de archivo
        return (
          <Box sx={{ my: 2, textAlign: 'center' }}>
            <Button
              variant="outlined"
              color="primary"
              href={asset.url || asset.fields?.file?.url || ''}
              target="_blank"
              rel="noopener noreferrer"
            >
              Descargar archivo: {asset.title || 'Archivo'}
            </Button>
          </Box>
        );
      },
      'table': (node, children) => (
        <Box sx={{
          overflowX: 'auto',
          mt: { xs: 2, sm: 4 },
          borderRadius: '3px',
          boxShadow: 2,
          background: '#fff',
          border: '1px solid #e0e0e0',
          maxWidth: 1440,
          mx: 'auto',
        }}>
          <table style={{ width: '100%', maxWidth: 1440, borderCollapse: 'collapse', textAlign: 'left', margin: '0 auto', borderRadius: '3px', overflow: 'hidden', color: color }}>
            <tbody>
              {children}
            </tbody>
          </table>
        </Box>
      ),
      'table-header-cell': (node, children) => (
        <th style={{
          background: headerColor,
          color: color || '#fff',
          padding: '12px 16px',
          fontWeight: 700,
          fontSize: '1rem',
          borderBottom: '2px solid #e0e0e0',
          textAlign: 'left',
        }}>{children}</th>
      ),
      'table-row': (node, children) => (
        <tr style={{ verticalAlign: 'top', color }}>{children}</tr>
      ),
      'table-cell': (node, children) => (
        <td style={{
          padding: '10px 14px',
          borderBottom: '1px solid #eee',
          fontSize: '1rem',
          verticalAlign: 'top',
          textAlign: 'left',
          color,
        }}>{children}</td>
      ),
    },
  };

  return (
    <Box sx={{ m: 5 }}>
      {title && (
        <Typography variant="h5" gutterBottom sx={{ color }}>
          {title}
        </Typography>
      )}
      <Typography variant="body1" component="div" sx={{ color }}>
        {documentToReactComponents(content, options)}
      </Typography>
    </Box>
  );
};

RichTextBlock.propTypes = {
  content: PropTypes.object.isRequired,
  title: PropTypes.string,
  color: PropTypes.string,
};

export default RichTextBlock;